// Sales Page Component
// frontend/src/testing/Page/SalesPage.jsx
import React, { useState } from 'react';
import { 
  DollarSign, 
  MapPin, 
  Check, 
  ChevronRight, 
  CreditCard, 
  Globe, 
  UserCheck, 
  Clock, 
  AlertCircle,
  ArrowRight,
  Calendar,
  Download,
  Mail,
  Star
} from 'lucide-react';
const SalesPage = () => {
    const [selectedBudget, setSelectedBudget] = useState(20000);
    const [estimatedLeads, setEstimatedLeads] = useState(100);
    
    const updateLeadEstimate = (budget) => {
      setSelectedBudget(budget);
      // Assuming average cost per lead of $200
      setEstimatedLeads(Math.floor(budget / 200));
    };
    
    return (
      <div className="bg-gradient-to-b from-blue-50 to-white min-h-screen">
        {/* Navigation */}
        <nav className="bg-white shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between h-16">
              <div className="flex">
                <div className="flex-shrink-0 flex items-center">
                  <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center mr-3">
                    <svg viewBox="0 0 200 200" className="w-6 h-6 text-white">
                      <circle cx="100" cy="100" r="100" fill="#1e40af"/>
                      <path d="M120,40C150,40 160,15 185,15C210,15 225,25 225,40C225,55 215,65 200,65L120,65C105,65 95,55 95,40C95,25 105,15 120,15Z" fill="#ffffff" transform="scale(0.4) translate(40, 80)"/>
                      <path d="M95,40C85,40 75,30 70,15C90,20 95,30 95,40Z" fill="#ffffff" transform="scale(0.4) translate(40, 80)"/>
                      <circle cx="185" cy="35" r="5" fill="#000000" transform="scale(0.4) translate(40, 80)"/>
                      <path d="M185,15L185,0L190,0L190,15Z" fill="#60a5fa" transform="scale(0.4) translate(40, 80)"/>
                    </svg>
                  </div>
                  <span className="text-xl font-bold text-blue-900">Annuity Whale</span>
                </div>
              </div>
              <div className="flex items-center">
                <a href="#pricing" className="px-3 py-2 text-sm font-medium text-gray-700 hover:text-blue-600">Pricing</a>
                <a href="#how-it-works" className="px-3 py-2 text-sm font-medium text-gray-700 hover:text-blue-600">How It Works</a>
                <a href="#success-stories" className="px-3 py-2 text-sm font-medium text-gray-700 hover:text-blue-600">Success Stories</a>
                <a href="#contact" className="ml-4 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700">Contact Us</a>
              </div>
            </div>
          </div>
        </nav>
  
        {/* Hero Section */}
        <div className="relative overflow-hidden">
          <div className="max-w-7xl mx-auto">
            <div className="relative z-10 pb-8 bg-transparent sm:pb-16 md:pb-20 lg:max-w-2xl lg:w-full lg:pb-28 xl:pb-32">
              <main className="mt-10 mx-auto max-w-7xl px-4 sm:mt-12 sm:px-6 md:mt-16 lg:mt-20 lg:px-8 xl:mt-28">
                <div className="sm:text-center lg:text-left">
                  <h1 className="text-4xl tracking-tight font-extrabold text-gray-900 sm:text-5xl md:text-6xl">
                    <span className="block xl:inline">Exclusive Annuity Leads</span>{' '}
                    <span className="block text-blue-600 xl:inline">Your Budget, Your Territory</span>
                  </h1>
                  <p className="mt-3 text-base text-gray-500 sm:mt-5 sm:text-lg sm:max-w-xl sm:mx-auto md:mt-5 md:text-xl lg:mx-0">
                    No subscriptions. No competition. Get all the annuity leads in your chosen territory until your budget is depleted. Simple, transparent, and effective.
                  </p>
                  <div className="mt-5 sm:mt-8 sm:flex sm:justify-center lg:justify-start">
                    <div className="rounded-md shadow">
                      <a
                        href="#pricing"
                        className="w-full flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 md:py-4 md:text-lg md:px-10"
                      >
                        Get Started
                      </a>
                    </div>
                    <div className="mt-3 sm:mt-0 sm:ml-3">
                      <a
                        href="#how-it-works"
                        className="w-full flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 md:py-4 md:text-lg md:px-10"
                      >
                        How It Works
                      </a>
                    </div>
                  </div>
                </div>
              </main>
            </div>
          </div>
          <div className="hidden lg:block lg:absolute lg:inset-y-0 lg:right-0 lg:w-1/2">
            <img
              className="h-56 w-full object-cover sm:h-72 md:h-96 lg:w-full lg:h-full"
              src="https://images.unsplash.com/photo-1557804506-669a67965ba0?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=1267&q=80"
              alt="Business meeting"
            />
          </div>
        </div>
  
        {/* How It Works Section */}
        <div id="how-it-works" className="py-16 bg-white overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="lg:text-center">
              <h2 className="text-base text-blue-600 font-semibold tracking-wide uppercase">How It Works</h2>
              <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
                Simple Process, Powerful Results
              </p>
              <p className="mt-4 max-w-2xl text-xl text-gray-500 lg:mx-auto">
                Our straightforward system delivers high-quality annuity leads directly to you without the complexity of subscriptions.
              </p>
            </div>
  
            <div className="mt-10">
              <dl className="space-y-10 md:space-y-0 md:grid md:grid-cols-3 md:gap-x-8 md:gap-y-10">
                <div className="relative">
                  <dt>
                    <div className="absolute flex items-center justify-center h-12 w-12 rounded-md bg-blue-500 text-white">
                      <DollarSign className="h-6 w-6" />
                    </div>
                    <p className="ml-16 text-lg leading-6 font-medium text-gray-900">1. Choose Your Budget</p>
                  </dt>
                  <dd className="mt-2 ml-16 text-base text-gray-500">
                    Start with a minimum of $20,000. The larger your budget, the more territories you can claim exclusively.
                  </dd>
                </div>
  
                <div className="relative">
                  <dt>
                    <div className="absolute flex items-center justify-center h-12 w-12 rounded-md bg-blue-500 text-white">
                      <MapPin className="h-6 w-6" />
                    </div>
                    <p className="ml-16 text-lg leading-6 font-medium text-gray-900">2. Select Your Territory</p>
                  </dt>
                  <dd className="mt-2 ml-16 text-base text-gray-500">
                    Choose counties, states, or nationwide coverage. You'll receive all leads from your selected areas.
                  </dd>
                </div>
  
                <div className="relative">
                  <dt>
                    <div className="absolute flex items-center justify-center h-12 w-12 rounded-md bg-blue-500 text-white">
                      <Check className="h-6 w-6" />
                    </div>
                    <p className="ml-16 text-lg leading-6 font-medium text-gray-900">3. Get Exclusive Leads</p>
                  </dt>
                  <dd className="mt-2 ml-16 text-base text-gray-500">
                    Receive verified annuity leads directly to your inbox until your budget is depleted. No competition.
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        </div>
  
        {/* Pricing Calculator Section */}
        <div id="pricing" className="py-16 bg-blue-50 overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="lg:text-center mb-12">
              <h2 className="text-base text-blue-600 font-semibold tracking-wide uppercase">Pricing</h2>
              <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
                Your Budget, Your Territory
              </p>
              <p className="mt-4 max-w-2xl text-xl text-gray-500 lg:mx-auto">
                Our simple pricing model gives you complete control. Calculate your expected lead volume below.
              </p>
            </div>
  
            <div className="bg-white rounded-lg shadow-xl overflow-hidden">
              <div className="px-6 py-8 sm:p-10">
                <div className="flex items-center justify-between">
                  <h3 className="text-2xl font-medium text-gray-900">Lead Budget Calculator</h3>
                  <span className="px-3 py-1 text-sm font-semibold leading-5 rounded-full bg-blue-100 text-blue-800">
                    No Subscriptions
                  </span>
                </div>
                
                <div className="mt-6">
                  <div className="flex flex-col space-y-4">
                    <div>
                      <label htmlFor="budget" className="block text-sm font-medium text-gray-700">
                        Select Your Budget
                      </label>
                      <select
                        id="budget"
                        className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                        value={selectedBudget}
                        onChange={(e) => updateLeadEstimate(parseInt(e.target.value))}
                      >
                        <option value="20000">$20,000 - County Level</option>
                        <option value="50000">$50,000 - State Level</option>
                        <option value="100000">$100,000 - Multi-State</option>
                        <option value="200000">$200,000 - Nationwide</option>
                      </select>
                    </div>
                    
                    <div className="bg-blue-50 p-4 rounded-md">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-gray-500">Estimated Leads</p>
                          <p className="text-3xl font-bold text-gray-900">{estimatedLeads}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Average Cost Per Lead</p>
                          <p className="text-3xl font-bold text-gray-900">$200</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="border-t border-gray-200 pt-4">
                      <h4 className="text-lg font-medium text-gray-900">What You Get:</h4>
                      <ul className="mt-4 space-y-2">
                        <li className="flex items-start">
                          <Check className="h-5 w-5 text-green-500 mr-2" />
                          <span className="text-gray-600">Exclusive rights to all leads in your territory</span>
                        </li>
                        <li className="flex items-start">
                          <Check className="h-5 w-5 text-green-500 mr-2" />
                          <span className="text-gray-600">Verified, high-quality annuity prospects</span>
                        </li>
                        <li className="flex items-start">
                          <Check className="h-5 w-5 text-green-500 mr-2" />
                          <span className="text-gray-600">No recurring fees or subscriptions</span>
                        </li>
                        <li className="flex items-start">
                          <Check className="h-5 w-5 text-green-500 mr-2" />
                          <span className="text-gray-600">Full lead details (contact info, estimated value)</span>
                        </li>
                        <li className="flex items-start">
                          <Check className="h-5 w-5 text-green-500 mr-2" />
                          <span className="text-gray-600">Budget dashboard to track consumption</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
              <div className="px-6 pt-6 pb-8 bg-gray-50 sm:px-10 sm:py-10">
                <div className="flex justify-center">
                  <a
                    href="/payment"
                    className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                  >
                    Secure Your Territory
                    <ChevronRight className="ml-2 h-4 w-4" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
  
        {/* Success Stories Section */}
        <div id="success-stories" className="py-16 bg-white overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="lg:text-center">
              <h2 className="text-base text-blue-600 font-semibold tracking-wide uppercase">Success Stories</h2>
              <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
                Real Results From Real Agents
              </p>
              <p className="mt-4 max-w-2xl text-xl text-gray-500 lg:mx-auto">
                Don't take our word for it. Here are real success stories from financial advisors using our system.
              </p>
            </div>
  
            <div className="mt-12 grid gap-8 lg:grid-cols-3">
              <div className="bg-blue-50 rounded-xl p-8 shadow-md">
                <div className="flex items-center mb-4">
                  <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                    <UserCheck className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-medium text-gray-900">Michael R.</h3>
                    <p className="text-sm text-gray-500">Phoenix, AZ</p>
                  </div>
                </div>
                <p className="text-gray-600">
                  "I invested $50,000 in the state of Arizona and converted 9 high-value annuity contracts worth over $3.2 million. That's a 64:1 return on my marketing investment."
                </p>
                <div className="mt-4">
                  <p className="text-sm text-gray-500">Results:</p>
                  <p className="text-lg font-bold text-green-600">$3.2M in Contracts</p>
                </div>
              </div>
  
              <div className="bg-blue-50 rounded-xl p-8 shadow-md">
                <div className="flex items-center mb-4">
                  <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                    <UserCheck className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-medium text-gray-900">Sarah T.</h3>
                    <p className="text-sm text-gray-500">Raleigh, NC</p>
                  </div>
                </div>
                <p className="text-gray-600">
                  "The county-level exclusivity was a game-changer. I focused on 3 wealthy counties with my $20,000 budget and landed 4 contracts that completely transformed my practice."
                </p>
                <div className="mt-4">
                  <p className="text-sm text-gray-500">Results:</p>
                  <p className="text-lg font-bold text-green-600">$1.8M in Contracts</p>
                </div>
              </div>
  
              <div className="bg-blue-50 rounded-xl p-8 shadow-md">
                <div className="flex items-center mb-4">
                  <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                    <UserCheck className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-medium text-gray-900">James L.</h3>
                    <p className="text-sm text-gray-500">Denver, CO</p>
                  </div>
                </div>
                <p className="text-gray-600">
                  "I went all in with $100,000 covering Colorado and Wyoming. Within 3 months, I had closed deals worth over $5 million in total contract value. Best marketing decision ever."
                </p>
                <div className="mt-4">
                  <p className="text-sm text-gray-500">Results:</p>
                  <p className="text-lg font-bold text-green-600">$5M+ in Contracts</p>
                </div>
              </div>
            </div>
          </div>
        </div>
  
        {/* CTA Section */}
        <div className="bg-blue-700">
          <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:py-16 lg:px-8 lg:flex lg:items-center lg:justify-between">
            <h2 className="text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
              <span className="block">Ready to grow your annuity business?</span>
              <span className="block text-blue-100">Secure your exclusive territory today.</span>
            </h2>
            <div className="mt-8 flex lg:mt-0 lg:flex-shrink-0">
              <div className="inline-flex rounded-md shadow">
                <a
                  href="/payment"
                  className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-blue-600 bg-white hover:bg-blue-50"
                >
                  Get Started
                </a>
              </div>
              <div className="ml-3 inline-flex rounded-md shadow">
                <a
                  href="#contact"
                  className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  Learn More
                </a>
              </div>
            </div>
          </div>
        </div>
  
        {/* Footer */}
        <footer className="bg-gray-800">
          <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:py-16 lg:px-8">
            <div className="xl:grid xl:grid-cols-3 xl:gap-8">
              <div className="space-y-8 xl:col-span-1">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center mr-3">
                    <svg viewBox="0 0 200 200" className="w-6 h-6">
                      <circle cx="100" cy="100" r="100" fill="#1e40af"/>
                      <path d="M120,40C150,40 160,15 185,15C210,15 225,25 225,40C225,55 215,65 200,65L120,65C105,65 95,55 95,40C95,25 105,15 120,15Z" fill="#ffffff" transform="scale(0.4) translate(40, 80)"/>
                      <path d="M95,40C85,40 75,30 70,15C90,20 95,30 95,40Z" fill="#ffffff" transform="scale(0.4) translate(40, 80)"/>
                      <circle cx="185" cy="35" r="5" fill="#000000" transform="scale(0.4) translate(40, 80)"/>
                      <path d="M185,15L185,0L190,0L190,15Z" fill="#60a5fa" transform="scale(0.4) translate(40, 80)"/>
                    </svg>
                  </div>
                  <span className="text-xl font-bold text-white">Annuity Whale</span>
                </div>
                <p className="text-gray-300 text-base">
                  Connecting financial advisors with exclusive, high-quality annuity leads. No subscriptions, no competition, just results.
                </p>
                <div className="flex space-x-6">
                  <a href="#" className="text-gray-400 hover:text-gray-300">
                    <span className="sr-only">LinkedIn</span>
                    <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                    </svg>
                  </a>
                  <a href="#" className="text-gray-400 hover:text-gray-300">
                    <span className="sr-only">Twitter</span>
                    <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
                    </svg>
                  </a>
                </div>
              </div>
              <div className="mt-12 grid grid-cols-2 gap-8 xl:mt-0 xl:col-span-2">
                <div className="md:grid md:grid-cols-2 md:gap-8">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-400 tracking-wider uppercase">Solutions</h3>
                    <ul className="mt-4 space-y-4">
                      <li>
                        <a href="#" className="text-base text-gray-300 hover:text-white">
                          County Leads
                        </a>
                      </li>
                      <li>
                        <a href="#" className="text-base text-gray-300 hover:text-white">
                          State Coverage
                        </a>
                      </li>
                      <li>
                        <a href="#" className="text-base text-gray-300 hover:text-white">
                          Nationwide Access
                        </a>
                      </li>
                    </ul>
                  </div>
                  <div className="mt-12 md:mt-0">
                    <h3 className="text-sm font-semibold text-gray-400 tracking-wider uppercase">Support</h3>
                    <ul className="mt-4 space-y-4">
                      <li>
                        <a href="#" className="text-base text-gray-300 hover:text-white">
                          FAQs
                        </a>
                      </li>
                      <li>
                        <a href="#" className="text-base text-gray-300 hover:text-white">
                          Contact
                        </a>
                      </li>
                      <li>
                        <a href="#" className="text-base text-gray-300 hover:text-white">
                          Documentation
                        </a>
                      </li>
                    </ul>
                  </div>
                </div>
                <div className="md:grid md:grid-cols-2 md:gap-8">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-400 tracking-wider uppercase">Company</h3>
                    <ul className="mt-4 space-y-4">
                      <li>
                        <a href="#" className="text-base text-gray-300 hover:text-white">
                          About
                        </a>
                      </li>
                      <li>
                        <a href="#" className="text-base text-gray-300 hover:text-white">
                          Blog
                        </a>
                      </li>
                      <li>
                        <a href="#" className="text-base text-gray-300 hover:text-white">
                          Press
                        </a>
                      </li>
                    </ul>
                  </div>
                  <div className="mt-12 md:mt-0">
                    <h3 className="text-sm font-semibold text-gray-400 tracking-wider uppercase">Legal</h3>
                    <ul className="mt-4 space-y-4">
                      <li>
                        <a href="#" className="text-base text-gray-300 hover:text-white">
                          Privacy
                        </a>
                      </li>
                      <li>
                        <a href="#" className="text-base text-gray-300 hover:text-white">
                          Terms
                        </a>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-12 border-t border-gray-700 pt-8">
              <p className="text-base text-gray-400 xl:text-center">
                &copy; 2025 Annuity Whale. All rights reserved.
              </p>
            </div>
          </div>
        </footer>
      </div>
    );
  }  
  
export default SalesPage;  